/**
 * Default view component.
 * @module components/theme/View/DefaultView
 */

import React from 'react';
import PropTypes from 'prop-types';

import { Container as SemanticContainer, Header, Image } from 'semantic-ui-react';
import config from '@plone/volto/registry';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';

import { hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { flattenToAppURL } from '@plone/volto/helpers';

/**
 * Component to display the default view.
 * @function DefaultView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const DefaultView = (props) => {
  const { content, location } = props;
  const path = getBaseUrl(location?.pathname || '');

  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;

  const contentLoaded = content && Object.keys(content).length > 0 && !(Object.keys(content).length === 1 && content.lock);

  if (!contentLoaded) return null;

  // Verifica se siamo nella Home page
  const isHomePage = path === '';

  // Prepara una versione dei contenuti senza i blocchi "title"
  let filteredContent = content;
  if (hasBlocksData(content)) {
    const blocksFieldname = Object.keys(content).find((key) => key === 'blocks' || key.endsWith('blocks'));
    const blocksLayoutFieldname = Object.keys(content).find((key) => key === 'blocks_layout' || key.endsWith('blocks_layout'));
    if (blocksFieldname && blocksLayoutFieldname) {
      const newBlocks = {};
      const newBlocksLayoutItems = [];
      content[blocksLayoutFieldname].items.forEach((blockId) => {
        const block = content[blocksFieldname][blockId];
        if (block['@type'] !== 'title') {
          newBlocks[blockId] = block;
          newBlocksLayoutItems.push(blockId);
        }
      });
      filteredContent = {
        ...content,
        [blocksFieldname]: newBlocks,
        [blocksLayoutFieldname]: {
          ...content[blocksLayoutFieldname],
          items: newBlocksLayoutItems,
        },
      };
    }
  }

  return (
    <Container id="page-document">
      {/* Titolo statico, ma NON in Home page */}
      {!isHomePage && content.title && (
        <Header as="h1" className="documentFirstHeading" style={{ marginBottom: '0.5em' }}>
          {content.title}
        </Header>
      )}

      {/* Descrizione */}
      {content.description && (
        <p className="documentDescription">
          {content.description}
        </p>
      )}

      {/* Immagine */}
      {content.preview_image && content.preview_image.download && (
        <div className="header-image-wrapper">
          <Image className="header-image responsive" src={flattenToAppURL(content.preview_image.download)} alt={content.title} fluid />
        </div>
      )}

      {/* Blocchi filtrati senza titolo */}
      {hasBlocksData(filteredContent) && (
        <RenderBlocks {...props} content={filteredContent} path={path} />
      )}
    </Container>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
DefaultView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    text: PropTypes.shape({
      data: PropTypes.string,
    }),
    image: PropTypes.shape({
      download: PropTypes.string,
    }),
  }).isRequired,
};

export default DefaultView;
